import { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { api, API_URL } from '@/services/api';
import { useAuthStore } from '@/store/auth.store';
import { useBrandingStore } from '@/store/branding.store';
import Button, { IconButton } from '@/components/common/Button';
import FaIcon from '@/components/common/FaIcon';
import MfaChallenge from './MfaChallenge';
import PasskeySignInButton from '@/components/auth/PasskeySignInButton';

// ---------------------------------------------------------------------------
// CSS-only background elements for atmosphere
// ---------------------------------------------------------------------------

function GridOverlay() {
  return (
    <div
      className="absolute inset-0 opacity-[0.07]"
      style={{
        backgroundImage:
          'linear-gradient(rgba(255,255,255,0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.1) 1px, transparent 1px)',
        backgroundSize: '64px 64px',
      }}
      aria-hidden="true"
    />
  );
}

function RadarSweep() {
  return (
    <div className="absolute inset-0 flex items-center justify-center pointer-events-none" aria-hidden="true">
      {/* Concentric rings — brighter so they actually register against the gradient. */}
      <div className="absolute w-[600px] h-[600px] sm:w-[800px] sm:h-[800px] rounded-full border border-orange-400/[0.12]" />
      <div className="absolute w-[400px] h-[400px] sm:w-[550px] sm:h-[550px] rounded-full border border-orange-400/[0.18]" />
      <div className="absolute w-[200px] h-[200px] sm:w-[300px] sm:h-[300px] rounded-full border border-orange-400/[0.22]" />
      {/* Sweep — vivid leading edge with a glow, wider trailing cone, faster rotation (animation is at 4s in index.css). */}
      <div className="absolute w-[300px] h-[300px] sm:w-[400px] sm:h-[400px] login-radar-sweep">
        <div
          className="absolute top-1/2 left-1/2 w-1/2 h-[2px] origin-left rounded-full"
          style={{
            background:
              'linear-gradient(90deg, rgba(249,115,22,0.95) 0%, rgba(249,115,22,0.4) 60%, transparent 100%)',
            boxShadow: '0 0 14px rgba(249,115,22,0.65)',
          }}
        />
        <div
          className="absolute top-1/2 left-1/2 w-1/2 h-1/2 origin-top-left"
          style={{
            background:
              'conic-gradient(from 0deg, rgba(249,115,22,0.35) 0deg, rgba(249,115,22,0.1) 25deg, transparent 55deg)',
            transform: 'rotate(-25deg)',
          }}
        />
      </div>
    </div>
  );
}

function FloatingParticles() {
  // Brand-aligned: orange accent + sky blue (picking up the logo's white bridge
  // on navy with the orange stripe), no more teal/cyan.
  // Particles are larger (2-3px) and brighter (50-90% peak via CSS animation +
  // box-shadow glow) so they read as actual moving motes rather than dust.
  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none" aria-hidden="true">
      <div className="login-particle absolute w-2.5 h-2.5 bg-orange-400 text-orange-400/80 rounded-full" style={{ top: '15%', left: '20%', animationDelay: '0s' }} />
      <div className="login-particle absolute w-2 h-2 bg-sky-300 text-sky-300/70 rounded-full" style={{ top: '70%', left: '75%', animationDelay: '2s' }} />
      <div className="login-particle absolute w-2.5 h-2.5 bg-orange-300 text-orange-300/70 rounded-full" style={{ top: '40%', left: '85%', animationDelay: '4s' }} />
      <div className="login-particle absolute w-2 h-2 bg-white text-white/60 rounded-full" style={{ top: '80%', left: '30%', animationDelay: '1s' }} />
      <div className="login-particle absolute w-2 h-2 bg-sky-300 text-sky-300/60 rounded-full" style={{ top: '25%', left: '60%', animationDelay: '3s' }} />
      <div className="login-particle absolute w-2.5 h-2.5 bg-orange-400 text-orange-400/70 rounded-full" style={{ top: '55%', left: '10%', animationDelay: '5s' }} />
      <div className="login-particle absolute w-2 h-2 bg-white text-white/55 rounded-full" style={{ top: '90%', left: '55%', animationDelay: '1.5s' }} />
      <div className="login-particle absolute w-2 h-2 bg-orange-300 text-orange-300/75 rounded-full" style={{ top: '10%', left: '45%', animationDelay: '3.5s' }} />
    </div>
  );
}

function ShieldLogo({ className }: { className?: string }) {
  return (
    <img
      src="/lightShield.png"
      alt="Command Bridge"
      className={className}
      style={{
        objectFit: 'contain',
        // Soft drop-shadow that picks up the orange accent in the wordmark,
        // so the shield lifts off the navy background.
        filter: 'drop-shadow(0 6px 18px rgba(249,115,22,0.18)) drop-shadow(0 2px 6px rgba(0,0,0,0.4))',
      }}
    />
  );
}

// Framer Motion variants for staggered entrance
const containerVariants = {
  hidden: {},
  visible: {
    transition: { staggerChildren: 0.1, delayChildren: 0.05 },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 16 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.5, ease: [0.25, 0.46, 0.45, 0.94] as const },
  },
};

function LoginBackground({ children }: { children: React.ReactNode }) {
  return (
    <div
      className="login-page min-h-screen relative flex items-center justify-center overflow-hidden"
      style={{
        // Navy palette pulled from the lightShield logo: deep base → mid-navy
        // (matching the shield's body) → warm shadow on the bottom-right where
        // the orange accent glow sits.
        background:
          'linear-gradient(135deg, #0f1d3a 0%, #1b2f5e 35%, #243d72 55%, #1a2447 80%, #14213d 100%)',
        paddingTop: 'max(2rem, env(safe-area-inset-top))',
        paddingBottom: 'max(2rem, env(safe-area-inset-bottom))',
      }}
    >
      {/* Warm orange glow behind the shield — same hue as the logo's accent stripe. */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            'radial-gradient(ellipse 55% 45% at 50% 38%, rgba(249,115,22,0.18) 0%, rgba(249,115,22,0.04) 35%, transparent 70%)',
        }}
        aria-hidden="true"
      />
      {/* Secondary cool glow on the lower-left to give depth. */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            'radial-gradient(ellipse 50% 40% at 15% 85%, rgba(56,128,200,0.18) 0%, transparent 65%)',
        }}
        aria-hidden="true"
      />
      <GridOverlay />
      <RadarSweep />
      <FloatingParticles />
      {/* Vignette */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background: 'radial-gradient(ellipse at center, transparent 50%, rgba(0,0,0,0.4) 100%)',
        }}
        aria-hidden="true"
      />
      <div className="relative z-10 w-full max-w-md px-6">{children}</div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Login form
// ---------------------------------------------------------------------------

interface LoginUser {
  id: string;
  email: string;
  name: string;
  phone?: string;
  job_title?: string;
  avatar_url?: string;
  onboarding_completed_at?: string | null;
  is_platform_admin?: boolean;
}

interface LoginSuccess {
  user: LoginUser;
  token: string;
  /** Present when the server started a refreshable console session (P4-07). */
  refresh_token?: string;
}

interface LoginMfaPending {
  mfa_required: true;
  mfa_pending_token: string;
  mfa_enrolled: boolean;
}

type LoginResponse = LoginSuccess | LoginMfaPending;

export default function Login() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const setToken = useAuthStore((s) => s.setToken);
  const setUser = useAuthStore((s) => s.setUser);
  const setPermissions = useAuthStore((s) => s.setPermissions);
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const { appName } = useBrandingStore();

  const returnUrl = searchParams.get('returnUrl');
  // Why the user landed on /login — set by the api 401 handler (`expired`) and
  // the idle-timeout logout (`idle`). Surfaced as an informational banner.
  const reason = searchParams.get('reason');
  const reasonCode = searchParams.get('code');
  const reasonMessage =
    reason === 'revoked' && reasonCode === 'DEVICE_REPORTED_LOST'
      ? 'This device was reported lost or stolen, so it was signed out and its offline data was cleared. If you have it back, sign in again or contact your administrator.'
      : reason === 'idle'
      ? 'You were signed out due to inactivity. Please sign in again.'
      : reason === 'expired'
        ? 'Your session expired. Please sign in again.'
        : reason === 'revoked'
          ? 'Your access on this device was ended (session revoked, membership removed or account disabled). Offline data on this device was cleared. Please sign in again.'
          : null;
  const displayName = appName || 'Command Bridge';

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  /*
   * Let people SEE the password they are typing.
   *
   * `/api/auth/login` is behind a per-account lockout with escalating
   * cooldowns, so a few blind mistypes on a phone keypad -- in the rain, in a
   * vehicle, in gloves -- do not just annoy, they lock the responder out of the
   * platform during the event they were called to. SetupAccount already offers
   * the toggle; the screen people actually sign in from did not.
   */
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [mfaPending, setMfaPending] = useState<LoginMfaPending | null>(null);
  const [ssoHint, setSsoHint] = useState<{ ssoUrl: string; tenantSlug: string } | null>(null);
  const ssoCheckTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Debounced SSO email-domain probe. Hits the public /api/sso/check endpoint
  // and, if the email's domain matches a tenant's configured email_domain,
  // surfaces a "sign in with SSO" CTA. Local password sign-in stays available.
  useEffect(() => {
    if (ssoCheckTimer.current) {
      clearTimeout(ssoCheckTimer.current);
      ssoCheckTimer.current = null;
    }
    const trimmed = email.trim();
    const at = trimmed.indexOf('@');
    const isComplete = at > 0 && /\.[a-zA-Z]{2,}$/.test(trimmed.slice(at + 1));
    if (!isComplete) {
      setSsoHint(null);
      return;
    }
    /*
     * The cleanup cancels a pending timer but not a request already in flight,
     * and this probe's answer is acted on: `startSso()` redirects to
     * `ssoHint.tenantSlug`'s identity provider.
     *
     * Two complete-looking addresses typed in quick succession ("a@b.com" then
     * "a@bc.com") both fire. If the first response lands second, the button
     * describes the wrong domain — and clicking it sends the user to a
     * different tenant's IdP than the address on screen. Drop a superseded
     * answer instead of applying it.
     */
    let cancelled = false;

    ssoCheckTimer.current = setTimeout(async () => {
      try {
        const result = await api.get<{ sso_url: string | null; tenant_slug?: string }>(
          '/sso/check',
          { email: trimmed },
        );
        if (cancelled) return;
        if (result.sso_url && result.tenant_slug) {
          setSsoHint({ ssoUrl: result.sso_url, tenantSlug: result.tenant_slug });
        } else {
          setSsoHint(null);
        }
      } catch {
        if (cancelled) return;
        setSsoHint(null);
      }
    }, 500);

    return () => {
      cancelled = true;
      if (ssoCheckTimer.current) clearTimeout(ssoCheckTimer.current);
    };
  }, [email]);

  function startSso() {
    if (!ssoHint) return;
    const params = new URLSearchParams();
    if (returnUrl) params.set('returnUrl', returnUrl);
    const qs = params.toString();
    const target = `${API_URL}/api/sso/${ssoHint.tenantSlug}/login${qs ? `?${qs}` : ''}`;
    window.location.href = target;
  }

  // If already authenticated, bounce straight to dashboard / returnUrl.
  useEffect(() => {
    if (isAuthenticated) {
      navigate(returnUrl || '/', { replace: true });
    }
  }, [isAuthenticated, navigate, returnUrl]);

  async function finishLogin(result: LoginSuccess) {
    setToken(result.token, result.refresh_token);
    setUser({
      id: result.user.id,
      email: result.user.email,
      name: result.user.name,
      phone: result.user.phone,
      job_title: result.user.job_title,
      avatar_url: result.user.avatar_url,
      onboarding_completed_at: result.user.onboarding_completed_at,
      is_platform_admin: result.user.is_platform_admin,
    });

    // PERMS-001: The login response doesn't include permissions. Fetch them
    // from /auth/me so `hasPermission()` works for non-platform-admin users.
    // Tolerate failure — the App boot-time effect will retry on next render
    // and the api interceptor handles 401s.
    try {
      const me = await api.get<{ data: { permissions: { module: string; action: string }[] } }>(
        '/auth/me',
      );
      setPermissions(me.data?.permissions ?? []);
    } catch {
      // Swallow — boot-time refresh in App.tsx will retry.
    }

    navigate(returnUrl || '/', { replace: true });
  }

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!email.trim() || !password) return;
    setError(null);
    setSubmitting(true);

    try {
      const result = await api.post<LoginResponse>('/auth/login', {
        email: email.trim(),
        password,
      });

      if ('mfa_required' in result) {
        // Password was valid but MFA is required. Show the inline challenge —
        // password and email are cleared so they can't be re-submitted by mistake.
        setMfaPending(result);
        setPassword('');
        setSubmitting(false);
        return;
      }

      finishLogin(result);
    } catch (err: any) {
      const status = err?.response?.status;
      const msg = err?.response?.data?.error?.message;
      if (status === 423 || msg?.toLowerCase().includes('locked')) {
        setError(msg || 'This account is temporarily locked. Try again later or use a sign-in link.');
      } else if (status === 401) {
        setError(msg || 'Email or password is incorrect.');
      } else if (status === 429) {
        setError('Too many sign-in attempts. Please wait a minute and try again.');
      } else {
        setError(msg || 'Something went wrong. Please try again.');
      }
      setSubmitting(false);
    }
  }

  return (
    <LoginBackground>
      <motion.div
        className="text-center"
        variants={containerVariants}
        initial="hidden"
        animate="visible"
      >
        <motion.div variants={itemVariants}>
          <ShieldLogo className="w-16 h-16 sm:w-20 sm:h-20 mx-auto mb-4" />
        </motion.div>

        <motion.h1
          variants={itemVariants}
          className="text-2xl sm:text-3xl font-bold text-white tracking-tight mb-1"
        >
          {displayName}
        </motion.h1>

        <motion.p
          variants={itemVariants}
          className="text-sm text-orange-200/75 tracking-wide mb-8"
        >
          Emergency Operations Platform
        </motion.p>

        {mfaPending ? (
          <motion.div variants={itemVariants}>
            <MfaChallenge
              mfaPendingToken={mfaPending.mfa_pending_token}
              enrolled={mfaPending.mfa_enrolled}
              onSuccess={(resp) => finishLogin(resp)}
              onCancel={() => {
                setMfaPending(null);
                setEmail('');
              }}
            />
          </motion.div>
        ) : (
        <motion.div
          variants={itemVariants}
          className="rounded-2xl p-7 backdrop-blur-xl border border-white/10 text-left"
          style={{
            background: 'linear-gradient(135deg, rgba(255,255,255,0.07) 0%, rgba(255,255,255,0.03) 100%)',
            boxShadow: '0 8px 32px rgba(0,0,0,0.3), inset 0 1px 0 rgba(255,255,255,0.05)',
          }}
        >
          {reasonMessage && (
            <div
              role="status"
              className="mb-4 text-sm text-sky-100 bg-sky-500/10 border border-sky-400/30 rounded-lg px-3 py-2"
            >
              {reasonMessage}
            </div>
          )}
          <form onSubmit={onSubmit} className="space-y-4">
            <div>
              <label htmlFor="email" className="block text-xs font-medium text-gray-300 mb-2 uppercase tracking-wide">
                Email
              </label>
              <input
                id="email"
                type="email"
                autoComplete="username"
                autoCapitalize="off"
                autoCorrect="off"
                spellCheck={false}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={submitting}
                className="w-full px-4 py-3 rounded-lg bg-white/5 border border-white/10 text-white placeholder-gray-500 focus:outline-none focus:border-orange-400 focus:ring-2 focus:ring-orange-400/60 disabled:opacity-50"
                required
              />
            </div>

            <div>
              <label htmlFor="password" className="block text-xs font-medium text-gray-300 mb-2 uppercase tracking-wide">
                Password
              </label>
              <div className="relative">
                <input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  autoComplete="current-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  disabled={submitting}
                  className="w-full px-4 py-3 pr-12 rounded-lg bg-white/5 border border-white/10 text-white placeholder-gray-500 focus:outline-none focus:border-orange-400 focus:ring-2 focus:ring-orange-400/60 disabled:opacity-50"
                  required
                />
                <IconButton
                  icon={<FaIcon icon={showPassword ? 'eye-slash' : 'eye'} className="h-4 w-4" />}
                  label={showPassword ? 'Hide password' : 'Show password'}
                  variant="ghost"
                  size="sm"
                  onClick={() => setShowPassword((v) => !v)}
                  disabled={submitting}
                  /* This card is dark whatever the app theme is, so ghost's
                     light-mode greys would put dark text on a dark field. */
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white hover:bg-white/10"
                />
              </div>
            </div>

            {error && (
              <div
                role="alert"
                className="text-sm text-amber-200 bg-amber-500/10 border border-amber-500/30 rounded-lg px-3 py-2"
              >
                {error}
              </div>
            )}

            <Button
              type="submit"
              variant="primary"
              fullWidth
              disabled={submitting || !email.trim() || !password}
            >
              {submitting ? 'Signing In…' : 'Sign In'}
            </Button>

            {ssoHint && (
              <div className="text-sm text-sky-200 bg-sky-500/10 border border-sky-500/30 rounded-lg px-3 py-2 flex items-center justify-between gap-3">
                <span>Your organization uses single sign-on.</span>
                <button
                  type="button"
                  onClick={startSso}
                  className="text-xs font-medium text-sky-100 hover:text-white underline decoration-sky-400/50 underline-offset-2"
                >
                  Sign in with SSO →
                </button>
              </div>
            )}
          </form>

          {/* Divider */}
          <div className="relative my-5">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-white/10" />
            </div>
            <div className="relative flex justify-center">
              {/* gray-500 on this card measures 3.76:1 -- 12px is small text
                  and needs 4.5. gray-400 clears it without brightening the
                  divider into something that competes with the buttons. */}
              <span className="px-3 text-xs uppercase tracking-wider text-gray-400" style={{ background: 'rgba(10,22,40,0.6)' }}>
                or
              </span>
            </div>
          </div>

          <Link to="/auth/magic-request" className="block">
            <Button
              type="button"
              variant="outline"
              fullWidth
              className="text-gray-200 border-white/15 bg-transparent dark:bg-transparent shadow-none hover:bg-white/5"
            >
              Text or Email Me a Sign-In Link
            </Button>
          </Link>

          <PasskeySignInButton onSignedIn={(result) => finishLogin(result)} disabled={submitting} />

          <div className="mt-5 text-center">
            <Link
              to="/auth/magic-request"
              className="text-xs text-gray-400 hover:text-gray-200"
            >
              Forgot or never set a password?
            </Link>
          </div>
        </motion.div>
        )}

        {/* 3.79:1 at gray-500. This is a real claim about the product, not
            decoration, and it is the last line a new user reads. */}
        <motion.p variants={itemVariants} className="mt-6 text-xs text-gray-400">
          Field-ready · Offline-capable · NIMS-aligned
        </motion.p>
      </motion.div>
    </LoginBackground>
  );
}
